import type { QueryParamsSource } from '@warp-drive/core/types/params';
import type {
  ConstrainedRequestOptions,
  FindRecordOptions,
  FindRecordRequestOptions,
  QueryRequestOptions,
} from '@warp-drive/core/types/request';
import {
  findRecord as restFindRecord,
  query as restQuery,
} from '@warp-drive/utilities/rest';
import { camelize, pluralize } from '@warp-drive/utilities/string';

export type UmgRestQueryRequestOptions<RT = unknown> =
  QueryRequestOptions<RT> & {
    options: {
      recordType: string;
    };
  };

export type UmgRestFindRecordRequestOptions<RT = unknown> =
  FindRecordRequestOptions<RT> & {
    options: {
      recordType: string;
    };
  };

export function query(
  type: string,
  queryParams: QueryParamsSource = {},
  options: ConstrainedRequestOptions = {},
): UmgRestQueryRequestOptions {
  const { urlParamsSettings, ...requestOptions } = options;
  const request = restQuery(type, normalizeQueryParams(queryParams), {
    resourcePath: resourcePathForType(type),
    ...requestOptions,
    urlParamsSettings: {
      arrayFormat: 'bracket',
      ...urlParamsSettings,
    },
    backgroundReload: true,
  });
  const headers = new Headers(request.headers);

  headers.set('Accept', 'application/json');

  return {
    ...request,
    headers,
    options: {
      recordType: type,
    },
  };
}

export function findRecord(
  type: string,
  id: string,
  options: FindRecordOptions = {},
): UmgRestFindRecordRequestOptions {
  const request = restFindRecord(type, id, {
    resourcePath: resourcePathForType(type),
    backgroundReload: true,
    ...options,
  });
  const headers = new Headers(request.headers);

  headers.set('Accept', 'application/json');

  return {
    ...request,
    headers,
    options: {
      recordType: type,
    },
  };
}

function normalizeQueryParams(
  queryParams: QueryParamsSource,
): QueryParamsSource {
  if (queryParams instanceof URLSearchParams) {
    return queryParams;
  }

  return Object.fromEntries(
    Object.entries(queryParams)
      .filter(([, value]) => value !== undefined)
      .map(([key, value]) => [key, value === null ? '' : value]),
  );
}

function resourcePathForType(recordType: string): string {
  if (recordType.startsWith('a11y-')) {
    const name = recordType.slice('a11y-'.length);

    return `a11y/${pluralize(camelize(name))}`;
  }

  return pluralize(camelize(recordType));
}
